import { APP_ROLES, normalizeRole, normalizeRoles, type AppRole } from '../features/auth/rbac';

export type DeploySurface = 'website' | 'customer' | 'driver' | 'admin' | 'owner';

const SURFACE_VALUES: DeploySurface[] = ['website', 'customer', 'driver', 'admin', 'owner'];

export const getDeploySurface = (): DeploySurface => {
  const raw = String((import.meta as { env?: { VITE_DEPLOY_SURFACE?: string } }).env?.VITE_DEPLOY_SURFACE ?? '')
    .trim()
    .toLowerCase();
  if (SURFACE_VALUES.includes(raw as DeploySurface)) {
    return raw as DeploySurface;
  }
  return 'website';
};

/** Roles permitted to complete login on this installable / build. Owner always passes hasRole checks. */
export const getSurfaceAllowedRoles = (surface: DeploySurface): readonly AppRole[] => {
  switch (surface) {
    case 'customer':
      return [APP_ROLES.customer, APP_ROLES.driver];
    case 'driver':
      return [APP_ROLES.driver, APP_ROLES.owner];
    case 'admin':
      return [APP_ROLES.admin, APP_ROLES.owner];
    case 'owner':
      return [APP_ROLES.owner];
    case 'website':
    default:
      return [APP_ROLES.customer, APP_ROLES.driver, APP_ROLES.admin, APP_ROLES.owner];
  }
};

export const isRoleAllowedOnSurface = (roleOrUser: unknown, surface: DeploySurface): boolean => {
  let candidate: unknown = roleOrUser;
  if (roleOrUser && typeof roleOrUser === 'object' && !Array.isArray(roleOrUser)) {
    const userObj = roleOrUser as { roles?: unknown; role?: unknown };
    candidate = Array.isArray(userObj.roles) && userObj.roles.length > 0
      ? userObj.roles
      : (userObj.role ? [userObj.role] : []);
  }

  const normalized = normalizeRoles(candidate);
  if (normalized.length === 0) return false;
  if (normalized.includes(APP_ROLES.owner)) return true;
  const allowed = getSurfaceAllowedRoles(surface);
  return normalized.some((r) => allowed.includes(r));
};

export const surfaceRequiresPrivilegedLogin = (surface: DeploySurface): boolean =>
  surface === 'admin' || surface === 'owner';

export type WrongAppInfo = {
  appLabel: string;
  hint: string;
  webUrl?: string;
};

export const getWrongAppInfo = (userRole: unknown, surface: DeploySurface): WrongAppInfo | null => {
  if (isRoleAllowedOnSurface(userRole, surface)) return null;
  const role = normalizeRole(userRole);
  switch (role) {
    case APP_ROLES.customer:
      return {
        appLabel: 'Ninpo Customer',
        hint: 'Browse, cart, and checkout live in the Ninpo Snacks customer app.',
        webUrl: 'https://ninposnacks.com'
      };
    case APP_ROLES.driver:
      return {
        appLabel: 'Ninpo Driver',
        hint: 'In-store shopping and delivery run in the driver app.',
        webUrl: 'https://driver.ninposnacks.com'
      };
    case APP_ROLES.admin:
      return {
        appLabel: 'Ninpo Admin',
        hint: 'Store operations and approvals use the admin app.',
        webUrl: 'https://admin.ninposnacks.com'
      };
    case APP_ROLES.owner:
      return {
        appLabel: 'Ninpo Owner',
        hint: 'Use Owner console on web (/admin) or the owner mobile app — same catalog APIs.',
        webUrl: 'https://owner.ninposnacks.com'
      };
    default:
      return {
        appLabel: 'Ninpo',
        hint: 'This account cannot sign in on this app. Use the app that matches your role.'
      };
  }
};

export const SURFACE_APP_TITLE: Record<Exclude<DeploySurface, 'website'>, string> = {
  customer: 'Ninpo Customer',
  driver: 'Ninpo Driver',
  admin: 'Ninpo Admin',
  owner: 'Ninpo Owner'
};
